/**
 * ThemeBadges component for displaying multiple theme badges
 * Handles overflow and shows "+N more" indicator
 */

'use client';

import { Tag } from 'lucide-react';
import { useMemo } from 'react';
import { useAllThemes } from '../hooks';
import { ThemeBadge, type ThemeBadgeTheme } from './theme-badge';

export interface ThemeBadgesProps {
  /** Array of themes to display */
  themes: ThemeBadgeTheme[];
  /** Maximum number of badges to show before "+N more" */
  maxVisible?: number;
  /** Variant style for badges */
  variant?: 'default' | 'compact' | 'filter';
  /** Whether to show the tag icon */
  showIcon?: boolean;
  /** Whether badges are removable */
  removable?: boolean;
  /** Callback when a badge is removed */
  onRemove?: (themeId: string) => void;
  /** Additional CSS classes for container */
  className?: string;
}

/**
 * ThemeBadges displays a collection of theme badges with optional overflow indicator
 *
 * @example
 * ```tsx
 * <ThemeBadges
 *   themes={[
 *     { id: '1', name: 'Rail Freight Forward', color: '#2B5F9E' },
 *     { id: '2', name: 'Green Logistics', color: '#4CAF50' }
 *   ]}
 *   maxVisible={2}
 *   showIcon={true}
 * />
 * ```
 *
 * @example
 * ```tsx
 * <ThemeBadges
 *   themes={selectedThemes}
 *   variant="filter"
 *   removable={true}
 *   onRemove={(id) => handleRemoveTheme(id)}
 * />
 * ```
 */
export function ThemeBadges({
  themes,
  maxVisible = 2,
  variant = 'default',
  showIcon = true,
  removable = false,
  onRemove,
  className = '',
}: ThemeBadgesProps) {
  if (!themes || themes.length === 0) {
    return null;
  }

  // Use shared hook to fetch all themes with colours
  const { themesMap } = useAllThemes();

  // Enrich themes with fetched colours from the map
  const enrichedThemes = useMemo(() => {
    return themes.map(theme => {
      if (!theme || !theme.id) return theme;

      // If theme already has a colour, use it
      if (theme.color) return theme;

      // Otherwise, get it from the themes map
      const fetchedTheme = themesMap.get(theme.id);
      if (fetchedTheme?.color) {
        return { ...theme, color: fetchedTheme.color };
      }

      return theme;
    });
  }, [themes, themesMap]);

  const visibleThemes = enrichedThemes.slice(0, maxVisible);
  const remainingCount = enrichedThemes.length - maxVisible;

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {showIcon && <Tag className="h-3 w-3 text-muted-foreground" />}

      {visibleThemes.map(theme =>
        theme && theme.id && theme.name ? (
          <ThemeBadge
            key={theme.id}
            theme={theme}
            variant={variant}
            removable={removable}
            onRemove={onRemove}
          />
        ) : null
      )}

      {remainingCount > 0 && (
        <span className="text-xs text-muted-foreground">
          +{remainingCount} {remainingCount === 1 ? 'more' : 'more'}
        </span>
      )}
    </div>
  );
}

